/** 수업 관리 — 테이블 + 생성/삭제 + 학생 배정 */
import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { IoAddCircle, IoTrashOutline, IoPeopleOutline, IoClose } from 'react-icons/io5';
import academyService from '../../services/academyService';
import { useConsoleAcademy } from '../../components/console/ConsoleLayout';
import {
  PageHead, PageTitle, PrimaryBtn, GhostBtn, Card, Table, Empty, IconBtn,
  Overlay, Modal, ModalHead, ModalBody, ModalFoot, Field,
} from './consoleUi';

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const COLORS = ['#4F46E5', '#0EA5E9', '#22C55E', '#F59E0B', '#EF4444', '#EC4899'];

const ConsoleClassesPage = () => {
  const { academy } = useConsoleAcademy();
  const aid = academy.id;
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [creating, setCreating] = useState(false);
  const [roster, setRoster] = useState(null); // 배정 모달 대상 class
  const [saving, setSaving] = useState(false);

  // 생성 폼
  const [form, setForm] = useState({ name: '', teacherName: '', color: COLORS[0] });
  const [schedule, setSchedule] = useState([]);
  const [day, setDay] = useState('월');
  const [time, setTime] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        academyService.getClasses(aid),
        academyService.getStudents(aid),
      ]);
      setClasses(c);
      setStudents(s);
    } finally {
      setLoading(false);
    }
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ name: '', teacherName: '', color: COLORS[0] });
    setSchedule([]); setDay('월'); setTime('');
    setCreating(true);
  };

  const addSlot = () => {
    if (!time.trim()) return;
    setSchedule((s) => [...s, { day, time: time.trim() }]);
    setTime('');
  };

  const create = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await academyService.createClass(aid, { ...form, name: form.name.trim(), schedule });
      setCreating(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`'${c.name}' 수업을 삭제할까요?\n출석·과제·성적 기록도 함께 사라집니다.`)) return;
    await academyService.deleteClass(aid, c.id);
    await load();
  };

  const toggleStudent = async (c, sid) => {
    const cur = c.studentIds || [];
    const next = cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    await academyService.updateClass(aid, c.id, { studentIds: next, students: next.length });
    const updated = { ...c, studentIds: next, students: next.length };
    setRoster(updated);
    setClasses((list) => list.map((x) => (x.id === c.id ? updated : x)));
  };

  return (
    <>
      <PageHead>
        <PageTitle>
          <h2>수업 관리</h2>
          <p>전체 {classes.length}개 수업 · 시간표·담당·배정 학생을 관리하세요.</p>
        </PageTitle>
        <PrimaryBtn onClick={openCreate}><IoAddCircle size={18} /> 수업 만들기</PrimaryBtn>
      </PageHead>

      <Card $pad="0">
        {loading ? (
          <Empty>불러오는 중…</Empty>
        ) : classes.length === 0 ? (
          <Empty>개설된 수업이 없어요. ‘수업 만들기’로 시작하세요.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>수업명</th>
                <th>시간표</th>
                <th>담당</th>
                <th style={{ width: 90 }}>학생</th>
                <th style={{ width: 150, textAlign: 'right' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((c) => (
                <tr key={c.id}>
                  <td>
                    <NameCell>
                      <Dot $c={c.color || '#4F46E5'} />
                      <strong>{c.name}</strong>
                    </NameCell>
                  </td>
                  <td>
                    {(c.schedule || []).length
                      ? <Slots>{c.schedule.map((s, i) => <Slot key={i}>{s.day} {s.time}</Slot>)}</Slots>
                      : <span style={{ color: '#C4C9D2' }}>미설정</span>}
                  </td>
                  <td>{c.teacherName || '–'}</td>
                  <td>{(c.studentIds || []).length}명</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <GhostBtn onClick={() => setRoster(c)} style={{ padding: '7px 12px' }}>
                      <IoPeopleOutline size={16} /> 배정
                    </GhostBtn>
                    <IconBtn $danger onClick={() => remove(c)} title="삭제" style={{ marginLeft: 4 }}>
                      <IoTrashOutline size={18} />
                    </IconBtn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* 수업 생성 */}
      {creating && (
        <Overlay onClick={() => !saving && setCreating(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>수업 만들기 <IconBtn onClick={() => setCreating(false)}><IoClose size={18} /></IconBtn></ModalHead>
            <ModalBody>
              <Field>
                <span className="lab">수업명</span>
                <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field>
                <span className="lab">담당 강사</span>
                <input value={form.teacherName} onChange={(e) => setForm({ ...form, teacherName: e.target.value })} />
              </Field>
              <Field as="div">
                <span className="lab">시간표</span>
                <SlotRow>
                  <DayPick>
                    {DAYS.map((d) => <DayBtn key={d} type="button" $on={day === d} onClick={() => setDay(d)}>{d}</DayBtn>)}
                  </DayPick>
                  <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ width: 130 }} />
                  <GhostBtn type="button" onClick={addSlot}>추가</GhostBtn>
                </SlotRow>
                {schedule.length > 0 && (
                  <Slots style={{ marginTop: 10 }}>
                    {schedule.map((s, i) => (
                      <Slot key={i} as="button" onClick={() => setSchedule(schedule.filter((_, idx) => idx !== i))}>
                        {s.day} {s.time} <IoClose size={11} />
                      </Slot>
                    ))}
                  </Slots>
                )}
              </Field>
              <Field as="div">
                <span className="lab">색상</span>
                <ColorRow>
                  {COLORS.map((c) => (
                    <ColorDot key={c} $c={c} $on={form.color === c} onClick={() => setForm({ ...form, color: c })} />
                  ))}
                </ColorRow>
              </Field>
            </ModalBody>
            <ModalFoot>
              <GhostBtn onClick={() => setCreating(false)}>취소</GhostBtn>
              <PrimaryBtn onClick={create} disabled={saving || !form.name.trim()}>{saving ? '생성 중…' : '수업 생성'}</PrimaryBtn>
            </ModalFoot>
          </Modal>
        </Overlay>
      )}

      {/* 학생 배정 */}
      {roster && (
        <Overlay onClick={() => setRoster(null)}>
          <Modal $w="480px" onClick={(e) => e.stopPropagation()}>
            <ModalHead>‘{roster.name}’ 학생 배정 <IconBtn onClick={() => setRoster(null)}><IoClose size={18} /></IconBtn></ModalHead>
            <ModalBody>
              {students.length === 0 ? (
                <Empty>먼저 ‘학생 관리’에서 학생을 등록하세요.</Empty>
              ) : (
                <RosterList>
                  {students.map((s) => {
                    const on = (roster.studentIds || []).includes(s.id);
                    return (
                      <RosterItem key={s.id} $on={on} onClick={() => toggleStudent(roster, s.id)}>
                        <Check $on={on}>{on && '✓'}</Check>
                        <span>{s.name}</span>
                        {s.age ? <small>{s.age}세</small> : null}
                      </RosterItem>
                    );
                  })}
                </RosterList>
              )}
            </ModalBody>
            <ModalFoot>
              <PrimaryBtn onClick={() => setRoster(null)}>완료 ({(roster.studentIds || []).length}명)</PrimaryBtn>
            </ModalFoot>
          </Modal>
        </Overlay>
      )}
    </>
  );
};

export default ConsoleClassesPage;

const NameCell = styled.div` display: flex; align-items: center; gap: 10px; strong { font-weight: 700; color: #111827; } `;
const Dot = styled.span` width: 10px; height: 10px; border-radius: 50%; background: ${({ $c }) => $c}; flex-shrink: 0; `;
const Slots = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `;
const Slot = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; background: #EEF2FF; color: #4338CA;
  border-radius: 14px; font-size: 12px; font-weight: 600;
`;
const SlotRow = styled.div` display: flex; align-items: center; gap: 8px; flex-wrap: wrap; `;
const DayPick = styled.div` display: flex; gap: 4px; `;
const DayBtn = styled.button`
  width: 34px; height: 34px; border-radius: 8px; font-size: 13px; font-weight: 700;
  background: ${({ $on }) => ($on ? '#4A7CFE' : '#F3F4F6')}; color: ${({ $on }) => ($on ? '#fff' : '#6B7280')};
`;
const ColorRow = styled.div` display: flex; gap: 10px; `;
const ColorDot = styled.button`
  width: 28px; height: 28px; border-radius: 50%; background: ${({ $c }) => $c};
  box-shadow: ${({ $on }) => ($on ? '0 0 0 3px #fff, 0 0 0 5px currentColor' : 'none')};
  color: ${({ $c }) => $c};
`;
const RosterList = styled.div` display: flex; flex-direction: column; gap: 4px; `;
const RosterItem = styled.button`
  display: flex; align-items: center; gap: 11px; width: 100%;
  padding: 11px 12px; border-radius: 9px; text-align: left;
  background: ${({ $on }) => ($on ? '#F0F5FF' : '#fff')};
  span { font-size: 14px; font-weight: 600; color: #1F2937; }
  small { margin-left: auto; font-size: 12px; color: #9CA3AF; }
  &:hover { background: ${({ $on }) => ($on ? '#E6EEFF' : '#F9FAFB')}; }
`;
const Check = styled.span`
  width: 20px; height: 20px; border-radius: 6px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; color: #fff; font-weight: 800;
  background: ${({ $on }) => ($on ? '#4A7CFE' : '#fff')};
  border: 1.5px solid ${({ $on }) => ($on ? '#4A7CFE' : '#D1D5DB')};
`;
